import { create } from 'zustand';
import { AuthApi } from '@shared/api/rest';
import { ApiUtils, SessionStorageUtils } from '@shared/utils';
import { useNotificationStore } from '../misc';
import { RequestStrategyEnum } from '@shared/api/rest/models';
import type { LoginRequest, LoginResponse, TwoFactorRequest } from '@shared/api/rest/auth';
import { Constants } from '@shared/widgets';

interface LoginState {
  data: LoginResponse | null;
  error: string | null;
  success: boolean | null;
  loading: boolean;
  login: (request: LoginRequest, signal?: AbortSignal) => Promise<void>;
  twoFactor: (request: TwoFactorRequest, signal?: AbortSignal) => Promise<void>;
  clear: () => void;
}

export const useLoginStore = create<LoginState>((set) => ({
  data: null,
  error: null,
  success: false,
  loading: false,

  login: ApiUtils.wrapperApi(async (request: LoginRequest, signal?: AbortSignal) => {
    try {
      set({ loading: true, success: null, error: null });
      const response = await AuthApi.login(request, signal);
      if (response.data.status === 'allow') {
        SessionStorageUtils.setToken(response.data.token);
        set({ success: true, data: response.data, loading: false });
      } else if (response.data.status === 'auth') {
        set({ success: true, data: response.data, loading: false });
      } else {
        set({ success: false, error: 'Login failed', loading: false });
      }
    } catch (error) {
      set({ success: null, data: null, error: ApiUtils.getApiError(error), loading: false });
      useNotificationStore.getState().setError(Constants.LOGIN_ERROR_MESSAGE);
      throw error;
    }
  }, RequestStrategyEnum.Debounce),

  twoFactor: ApiUtils.wrapperApi(async (request: TwoFactorRequest, signal?: AbortSignal) => {
    try {
      set({ loading: true, success: null, error: null });
      const response = await AuthApi.twoFactor(request, signal);
      if (response.data.status === 'allow') {
        SessionStorageUtils.setToken(response.data.token);
        set({ success: true, data: response.data, loading: false });
      } else if (response.data.status === 'locked_out') {
        set({ success: true, data: response.data, loading: false });
      } else {
        set({ success: false, error: 'Login failed', loading: false });
      }
    } catch (error) {
      set({ success: null, data: null, error: ApiUtils.getApiError(error), loading: false });
      useNotificationStore.getState().setError(Constants.LOGIN_ERROR_MESSAGE);
      throw error;
    }
  }, RequestStrategyEnum.Debounce),

  clear: () => set({ data: null, error: null, success: false, loading: false }),
}));
